"use client";

import { MessageSquare, X, ChevronDown, Send, FileText, Image as ImageIcon, Paperclip, Loader2, AlertTriangle, Phone, ContactRound, HelpCircle, PhoneIncoming } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";

import { Button } from "@/app/_module/components/ui/button";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { sendSms } from "@/app/(authenticated)/communication/_modules/services/sendSms";
import type { SendSmsRequest } from "@/app/(authenticated)/communication/_modules/types/sendSms.types";
import { useSMSQueryInvalidation } from "@/app/_module/hooks/useSMSQueryInvalidation";
import { SelectTemplateButton } from "@/app/(authenticated)/settings/_modules/components/SelectTemplateButton";
import type { SmsTemplate } from "@/app/(authenticated)/settings/_modules/types/sms-settings";
import type { Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { a2pService } from "@/app/_module/services/a2pService";
import { A2P_STAGE_TITLE } from "@/app/_module/components/A2PGateNotice";
import { useTwilioDevice } from "@/app/_module/hooks/useTwilioDevice";
import { callService } from "@/app/_module/services/callService";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { toast } from "@/app/_module/components/ui/use-toast";
import { useContactCard } from "@/app/(authenticated)/settings/_modules/hooks/contact-card-queries";
import { useLeadCallingHours } from "@/app/_module/hooks/useLeadCallingHours";
import { getSmsFailureReason } from "@/app/_module/utils/smsFailureReason";
import { fillVariables, leadNameVariables } from "@/app/_module/utils/smsVariables";

interface SmsMessage {
  id: string;
  text: string;
  direction: "inbound" | "outbound";
  timestamp: Date;
  // Full carrier status spectrum to match the chat widget + context.
  status?: "sending" | "queued" | "sent" | "delivered" | "failed" | "undelivered";
  messageSid?: string;
  mediaUrls?: string[]; // MMS media attachments
  errorCode?: string | null; // carrier error code on a failed message
  errorMessage?: string | null; // raw error description on a failed message
  reactions?: string[]; // Emoji reactions on this message
}

// Quick reply suggestions
const QUICK_REPLIES = [
  "Thank you!",
  "I'll call you back soon",
  "Can we schedule a time to talk?",
  "I'll send you the details",
  "Sounds good!",
  "Got it, thanks!",
];

// Reaction emojis
const REACTION_EMOJIS = ["👍", "❤️", "😊", "👏", "🙏", "✅"];

// Common emoji pattern - matches single emojis or small emoji-only strings
const EMOJI_REGEX = /^[\p{Emoji}\p{Emoji_Modifier}\p{Emoji_Modifier_Base}\p{Emoji_Presentation}\p{Extended_Pictographic}\uFE0F\u200D]+$/u;

// Check if a message is just emojis (for reaction detection)
const isEmojiOnlyMessage = (text: string): boolean => {
  if (!text || text.length > 8) return false; // Max 8 chars to avoid long emoji strings
  const trimmed = text.trim();
  return EMOJI_REGEX.test(trimmed) && trimmed.length <= 8;
};

// Apple Tapback format detection: "Liked "message"", "Loved "message"", etc.
const TAPBACK_PATTERNS = [
  { pattern: /^Liked\s+"(.+)"$/i, emoji: "👍" },
  { pattern: /^Loved\s+"(.+)"$/i, emoji: "❤️" },
  { pattern: /^Laughed at\s+"(.+)"$/i, emoji: "😂" },
  { pattern: /^Emphasized\s+"(.+)"$/i, emoji: "‼️" },
  { pattern: /^Questioned\s+"(.+)"$/i, emoji: "❓" },
  { pattern: /^Disliked\s+"(.+)"$/i, emoji: "👎" },
];

interface ActiveSmsWidgetProps {
  leadName: string;
  phoneNumber: string;
  leadId?: string;
  messages: SmsMessage[];
  isMinimized: boolean;
  hasActiveCall?: boolean; // If true, position on left to avoid overlap with call widget
  onClose: () => void;
  onMinimize: () => void;
  onMaximize: () => void;
  // Second arg passes the Twilio messageSid so the realtime status_update
  // listener can later flip this exact bubble (sent → delivered/undelivered)
  // without needing a refresh or matching by text+timestamp heuristics.
  onMessageSent: (message: string, messageSid?: string) => void;
}

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Format timestamp as time (e.g., "2:34 PM")
const formatTime = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

// Get proxied URL for carrier media (Twilio URLs require authentication; so do Telnyx-hosted
// ones, except presigned links, which load as they are)
const getProxiedMediaUrl = (url: string): string => {
  const telnyxHosted = /^https:\/\/([a-z0-9-]+\.)*telnyx\.com\//i.test(url) && !/[?&]X-Amz-Signature=/i.test(url);
  // If it's carrier-hosted, proxy it through our backend
  if (url.includes('twilio.com') || url.includes('api.twilio.com') || telnyxHosted) {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';
    return `${apiUrl}/api/communication/sms/media-proxy?url=${encodeURIComponent(url)}`;
  }
  // For non-Twilio URLs, return as-is
  return url;
};

export const ActiveSmsWidget = function ({
  leadName,
  phoneNumber,
  leadId,
  messages,
  isMinimized,
  hasActiveCall = false,
  onClose,
  onMinimize,
  onMaximize,
  onMessageSent,
}: ActiveSmsWidgetProps) {
  const router = useRouter();
  const { invalidateSMSQueries } = useSMSQueryInvalidation();
  const { data: phoneNumbers = [] } = useUserPhoneNumbers();
  const { user } = useUserContext();
  const {
    isMinimized: isDialerMinimized,
    openWidget: openDialerWidget,
    maximizeWidget: maximizeDialerWidget,
    setActiveTab: setDialerTab,
  } = useUnifiedDialer();
  const { data: contactCardData } = useContactCard();

  // ── Shell minimized chip: touch-draggable anywhere (same pattern as the
  // ScriptPanel chip). Position sticks while minimized; a real drag must not
  // ALSO fire the tap-to-maximize, so drags set a suppress flag the click
  // handler consumes. ──
  const [chipPos, setChipPos] = useState<{ x: number; y: number } | null>(null);
  const chipElRef = useRef<HTMLDivElement | null>(null);
  const chipTouchRef = useRef<{ startX: number; startY: number; origX: number; origY: number; moved: boolean } | null>(null);
  const chipDraggedRef = useRef(false);
  const onChipTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    const el = chipElRef.current;
    if (!t || !el) return;
    const r = el.getBoundingClientRect();
    chipTouchRef.current = { startX: t.clientX, startY: t.clientY, origX: r.left, origY: r.top, moved: false };
    const onMove = (ev: TouchEvent) => {
      const ref = chipTouchRef.current;
      const tt = ev.touches[0];
      if (!ref || !tt) return;
      const dx = tt.clientX - ref.startX;
      const dy = tt.clientY - ref.startY;
      if (!ref.moved && Math.abs(dx) + Math.abs(dy) > 6) {
        ref.moved = true;
        chipDraggedRef.current = true;
      }
      if (!ref.moved) return;
      ev.preventDefault();
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      setChipPos({
        x: Math.max(4, Math.min(ref.origX + dx, window.innerWidth - w - 4)),
        y: Math.max(40, Math.min(ref.origY + dy, window.innerHeight - h - 4)),
      });
    };
    const onEnd = () => {
      chipTouchRef.current = null;
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
  };

  const [newMessage, setNewMessage] = useState("");
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);

  // Twilio Device hook for browser-based calling
  const {
    isDeviceReady,
    isDeviceRegistering,
    connect,
    forwardCallsToPhone,
  } = useTwilioDevice();
  const [sending, setSending] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Track reactions per message (messageId -> array of emojis)
  const [messageReactions, setMessageReactions] = useState<Record<string, string[]>>({});
  // Track which inbound messages have been processed for auto-reaction detection
  const [processedReactionIds, setProcessedReactionIds] = useState<Set<string>>(new Set());
  // Lightbox state for viewing images fullscreen
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  // File attachment state for MMS
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [attachedPreview, setAttachedPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  // Toggle for showing the lead's phone number in the header. Hidden by default to keep the
  // header uncluttered; click the phone icon next to their name to reveal.
  const [showLeadPhone, setShowLeadPhone] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // A2P verification state
  const [a2pStatus, setA2pStatus] = useState<{ canSend: boolean; reason?: string; isLoading: boolean }>({
    canSend: true, // Default to true while loading to avoid flash
    isLoading: true,
  });

  // Check if user is a test user (bypass A2P requirement)
  const isTestUser = (user?.user as any)?.isTestUser === true;

  // Check A2P verification status on mount (skip for test users)
  useEffect(() => {
    const checkA2pStatus = async () => {
      // Test users bypass A2P verification
      if (isTestUser) {
        setA2pStatus({ canSend: true, isLoading: false });
        return;
      }

      try {
        const result = await a2pService.canSendSMS();
        setA2pStatus({ ...result, isLoading: false });
      } catch {
        // On error, default to allowing send (backend will block if needed)
        setA2pStatus({ canSend: true, isLoading: false });
      }
    };
    checkA2pStatus();
  }, [isTestUser]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!isMinimized && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isMinimized]);

  // Auto-detect emoji reactions from inbound messages
  // If client sends a single emoji or Apple Tapback, attach it as reaction to most recent outbound message
  useEffect(() => {
    if (messages.length < 2) return;

    // Find inbound messages that might be reactions
    messages.forEach((msg, idx) => {
      if (msg.direction !== "inbound" || processedReactionIds.has(msg.id)) return;

      const text = msg.text?.trim() || "";
      let detectedEmoji: string | null = null;
      let targetMessageId: string | null = null;

      // Check for Apple Tapback format: "Liked "original message""
      for (const tapback of TAPBACK_PATTERNS) {
        const match = text.match(tapback.pattern);
        if (match) {
          detectedEmoji = tapback.emoji;
          const quotedText = match[1];
          // Find the outbound message that matches the quoted text
          const matchingOutbound = messages.find(
            m => m.direction === "outbound" && m.text?.includes(quotedText)
          );
          if (matchingOutbound) {
            targetMessageId = matchingOutbound.id;
          }
          break;
        }
      }

      // Check for emoji-only message (client sent just an emoji as reply)
      if (!detectedEmoji && isEmojiOnlyMessage(text)) {
        detectedEmoji = text;
        // Find the most recent outbound message before this inbound
        for (let i = idx - 1; i >= 0; i--) {
          if (messages[i].direction === "outbound") {
            targetMessageId = messages[i].id;
            break;
          }
        }
      }

      // If we detected a reaction, add it to the target message
      if (detectedEmoji && targetMessageId) {
        setMessageReactions(prev => {
          const existing = prev[targetMessageId!] || [];
          if (existing.includes(detectedEmoji!)) return prev;
          const newReactions = [...existing, detectedEmoji!].slice(-3);
          return { ...prev, [targetMessageId!]: newReactions };
        });
        setProcessedReactionIds(prev => new Set([...prev, msg.id]));
      }
    });
  }, [messages, processedReactionIds]);

  // Handle file selection for MMS
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Belt to the disabled button's braces. The button is the normal entry
    // point, but the hidden <input> can still fire programmatically, and a
    // stale render could let a click through as `textingBlocked` flips. Tell
    // the agent WHY instead of silently accepting a file Send won't take.
    if (textingBlocked) {
      toast({
        title: "Outside texting hours",
        description: `It's ${hoursStatus?.localTime ?? "outside 8am–9pm"} for this lead. Texting is allowed 8am–9pm in their local time.`,
        variant: "destructive",
      });
      e.target.value = ""; // let them retry cleanly later
      return;
    }

    // Validate file type (images, videos, pdfs supported by MMS)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/3gpp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Unsupported file type. Please use images (JPG, PNG, GIF), videos (MP4), or PDF.');
      return;
    }

    // Validate file size (5MB max for MMS)
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB for MMS.');
      return;
    }

    setAttachedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setAttachedPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setAttachedPreview(null);
    }
  };

  // Remove attached file
  const handleRemoveAttachment = () => {
    setAttachedFile(null);
    setAttachedPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload file to storage and get public URL
  const uploadFileForMms = async (file: File): Promise<string> => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${apiUrl}/api/communication/sms/upload-media`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to upload media');
    }

    const data = await response.json();
    return data.data?.url || data.url;
  };

  // TCPA: lock texting when the lead is outside 8am–9pm their local time.
  const { status: hoursStatus } = useLeadCallingHours(leadId);
  const textingBlocked = hoursStatus ? !hoursStatus.allowed : false;

  const handleSendContactCard = async () => {
    if (!contactCardData?.contactCardVcfUrl) {
      toast({
        title: "No Contact Card",
        description: "Generate your contact card in Settings → SMS Settings → Contact Card",
        variant: "destructive",
      });
      return;
    }
    if (!phoneNumber) return;

    setSending(true);
    try {
      const result = await sendSms({
        to: phoneNumber,
        text: "Save my contact to always know it's me calling!",
        mediaUrl: contactCardData.contactCardVcfUrl,
      });
      if (!result) return; // sendSms already showed an error toast (e.g. A2P block)
      const fmtPhone = (n: string) => {
        const c = n.replace(/\D/g, "");
        if (c.length === 10) return `(${c.slice(0,3)}) ${c.slice(3,6)}-${c.slice(6)}`;
        if (c.length === 11 && c.startsWith("1")) return `(${c.slice(1,4)}) ${c.slice(4,7)}-${c.slice(7)}`;
        return n;
      };
      toast({
        title: "Contact Card Sent",
        description: `Contact card sent to ${fmtPhone(phoneNumber)}`,
      });
      invalidateSMSQueries();
      onMessageSent?.(
        "Save my contact to always know it's me calling!",
        (result as any)?.data?.messageSid,
      );
    } catch {
      toast({
        title: "Send Failed",
        description: "Failed to send contact card.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !attachedFile) || sending) return;
    if (textingBlocked) return; // outside calling hours (backend also enforces)

    const messageText = newMessage.trim();
    setNewMessage("");
    setSending(true);
    setUploading(!!attachedFile);

    try {
      let mediaUrl: string | undefined;

      // Upload file if attached
      if (attachedFile) {
        mediaUrl = await uploadFileForMms(attachedFile);
        setUploading(false);
      }

      const payload: SendSmsRequest = {
        to: phoneNumber,
        text: messageText || (attachedFile ? '[MMS Attachment]' : ''),
        mediaUrl, // Add media URL for MMS
      };

      const result = await sendSms(payload);

      if (result) {
        // Add message to conversation, threading the Twilio messageSid so
        // realtime status updates can target this bubble.
        const sentSid = (result as any)?.data?.messageSid as string | undefined;
        onMessageSent(messageText || '[MMS Attachment]', sentSid);

        // Clear attachment
        handleRemoveAttachment();

        // Invalidate SMS queries to refresh history
        await invalidateSMSQueries();
      } else {
        // sendSms returned null (A2P error or other failure)
        // Restore message text so agent can try again after fixing the issue
        setNewMessage(messageText);
      }
    } catch (error: any) {
      // Restore message text on error
      setNewMessage(messageText);
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Get user's default outbound phone from profile
  const userDefaultPhone = (user?.user as unknown as Record<string, unknown>)?.defaultOutboundPhone as string;

  // Select best outbound phone — mirrors the backend's send logic to keep the Eye indicator
  // accurate. For sole proprietor agents, only ONE of their numbers is A2P-registered (the one
  // with campaignLinkStatus === 'linked'), so the backend always swaps to that number. We
  // preferentially show that registered number here so what's displayed matches what sends.
  const selectBestOutboundPhone = (leadPhone: string): string => {
    // A2P-registered (campaign-linked) number always wins — this is what the backend will use
    // when a sole prop agent sends. For LVS agents, multiple numbers can be campaign-linked,
    // in which case we fall through to area-code matching among the linked ones.
    const a2pLinkedNumbers = phoneNumbers.filter((phone: any) => phone.campaignLinkStatus === 'linked');
    if (a2pLinkedNumbers.length === 1) {
      return a2pLinkedNumbers[0].phoneNumber;
    }

    const candidatePool = a2pLinkedNumbers.length > 0 ? a2pLinkedNumbers : phoneNumbers;

    // If user has a default phone and it's in the pool, use it
    if (userDefaultPhone && candidatePool.some((p: any) => p.phoneNumber === userDefaultPhone)) {
      return userDefaultPhone;
    }

    // Try to match area code within the candidate pool
    const leadAreaCode = leadPhone.replace(/\D/g, '').slice(0, 3);
    const matchingPhone = candidatePool.find((phone: any) => {
      const phoneAreaCode = phone.phoneNumber?.replace(/\D/g, '').slice(0, 3);
      return phoneAreaCode === leadAreaCode;
    });

    if (matchingPhone) {
      return matchingPhone.phoneNumber;
    }

    // Fallback to first available phone number in the candidate pool
    if (candidatePool.length > 0) {
      return candidatePool[0].phoneNumber;
    }

    return '';
  };

  // Select best caller ID using area code matching (same logic as selectBestOutboundPhone)
  const selectBestCallerId = (leadPhone: string): string | undefined => {
    // If user has a default phone, use it
    if (userDefaultPhone) {
      return userDefaultPhone;
    }

    // Try to match area code
    const leadAreaCode = leadPhone.replace(/\D/g, '').slice(0, 3);

    // Find phone with matching area code
    const matchingPhone = phoneNumbers.find((phone: any) => {
      const phoneAreaCode = phone.phoneNumber?.replace(/\D/g, '').slice(0, 3);
      return phoneAreaCode === leadAreaCode;
    });

    if (matchingPhone) {
      return matchingPhone.phoneNumber;
    }

    // Fallback to first available phone number
    if (phoneNumbers.length > 0) {
      return phoneNumbers[0].phoneNumber;
    }

    return undefined;
  };

  // Handle initiating a call to the contact
  const handleCall = async () => {
    if (!phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "This contact does not have a phone number.",
        variant: "destructive",
      });
      return;
    }

    if (phoneNumbers.length === 0) {
      toast({
        title: "No Active Phone Numbers",
        description: "You need at least one active phone number to make calls. Go to Settings > Phone Numbers to purchase one.",
        variant: "destructive",
      });
      return;
    }

    setIsInitiatingCall(true);

    try {
      // Check user preference: Phone forwarding or browser calling
      if (forwardCallsToPhone) {
        // Use phone-to-phone method (call forwarding)
        try {
          // Create session record FIRST for lead details phone forwarding
          await api.post({
            url: getApiUrl('/communication/sessions/manual-call'),
            body: {
              toNumber: phoneNumber,
              leadId: leadId || undefined,
            },
            auth: true,
          });

          const result = await callService.initiateCall({
            to: phoneNumber,
            from: undefined,
            lead_id: leadId || undefined,
            recording_enabled: true,
            metadata: {
              lead_name: leadName,
              source: "sms_widget_forwarding",
            }
          });

          if (result && result.call_id) {
            toast({
              title: "Call Forwarding",
              description: `Your phone will ring to connect to ${leadName}`,
            });
          }
        } catch (error) {
          toast({
            title: "Call Failed",
            description: "Failed to initiate call forwarding. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsInitiatingCall(false);
        }
        return;
      }

      // Use browser calling (modern method)
      if (!isDeviceReady) {
        toast({
          title: "Device Not Ready",
          description: isDeviceRegistering ? "Initializing calling device..." : "Calling device is not ready. Please refresh the page or enable call forwarding in Settings.",
          variant: "destructive",
        });
        setIsInitiatingCall(false);
        return;
      }

      // Request microphone permission before starting call
      const { requestMicrophoneWithGuidance } = await import("@/app/_module/utils/microphone-permission");
      const permissionResult = await requestMicrophoneWithGuidance();

      if (!permissionResult.granted) {
        toast({
          title: "Microphone Access Required",
          description: permissionResult.error || "Please allow microphone access to make calls.",
          variant: "destructive",
        });
        setIsInitiatingCall(false);
        return;
      }

      // Create session record FIRST to ensure database integrity
      await api.post({
        url: getApiUrl('/communication/sessions/manual-call'),
        body: {
          toNumber: phoneNumber,
          leadId: leadId,
        },
        auth: true,
      });

      // Shell: hand the screen to the CALL — minimize this chat sheet and
      // surface the dialer (same experience as the nav Call button). Without
      // this the call connected UNDER the full-screen SMS sheet.
      if (typeof window !== "undefined" && window.location.pathname.startsWith("/m")) {
        onMinimize();
        openDialerWidget();
        setDialerTab("call");
        maximizeDialerWidget();
      }

      // Use Twilio Voice SDK browser calling
      // Backend smart area code matching selects the best caller ID per lead
      const call = await connect({
        to: phoneNumber,
        leadId: leadId,
        metadata: {
          lead_name: leadName,
          source: "sms_widget",
        }
      });

      if (call) {
        toast({
          title: "Call Connected",
          description: `Calling ${leadName} at ${formatPhoneNumber(phoneNumber)}`,
        });
      }
    } catch (error) {
      let errorMessage = "Failed to initiate call. Please try again.";
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }

      toast({
        title: "Call Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsInitiatingCall(false);
    }
  };

  // Handle template selection with variable substitution
  const handleTemplateSelect = (template: SmsTemplate) => {
    setSelectedTemplateId(template.id);

    // Get the outbound phone number (area code matched or default)
    const outboundPhone = selectBestOutboundPhone(phoneNumber || '');

    // The widget only has the lead's name and phone. Email, address and the rest stay as
    // {{…}} and the server fills them from the lead at send.
    const variables: Record<string, string> = {
      // Basic Info, without the import's " (2)"
      ...leadNameVariables({ name: leadName }),
      // Contact Info
      recipient_phone: phoneNumber || '',
      // Agent Info
      phone: outboundPhone, // Your outbound phone (area code matched)
      my_phone: outboundPhone,
      agent_phone: outboundPhone,
      agency_name: (user?.currentAgency?.agency as any)?.name || '',
    };

    setNewMessage(fillVariables(template.content, variables));
  };

  // Handle quick reply selection
  const handleQuickReply = (reply: string) => {
    setNewMessage(reply);
    setShowQuickReplies(false);
  };

  // Handle reaction selection - add to message bubble AND send as emoji reply
  const handleReaction = async (emoji: string, messageId: string) => {
    // Add reaction to the message bubble visually (iMessage-style)
    setMessageReactions(prev => {
      const existing = prev[messageId] || [];
      // Toggle reaction - if already reacted with this emoji, remove it
      if (existing.includes(emoji)) {
        return {
          ...prev,
          [messageId]: existing.filter(e => e !== emoji),
        };
      }
      // Add reaction (max 3 unique reactions per message for clean UI)
      const newReactions = [...existing, emoji].slice(-3);
      return {
        ...prev,
        [messageId]: newReactions,
      };
    });

    // Also send emoji as a quick SMS reply
    setSending(true);
    try {
      const payload: SendSmsRequest = {
        to: phoneNumber,
        text: emoji,
      };

      const result = await sendSms(payload);

      if (result) {
        onMessageSent(emoji, (result as any)?.data?.messageSid);
        await invalidateSMSQueries();
      }
    } catch (error: any) {
      // Silent fail - reaction still shows on UI
    } finally {
      setSending(false);
    }
  };

  // Dynamic positioning based on active call state and dialer state
  // Chat widget should ALWAYS be positioned ABOVE the dialer widget
  // When dialer is minimized: "Open Dialer" button is ~48px tall + bottom-2 (8px) = 56px, so chat at bottom-[72px]
  // When dialer is maximized: Dialer is 600px tall + bottom-2 (8px) = 608px, so chat at bottom-[624px] max-[500px]:bottom-auto max-[500px]:top-[calc(env(safe-area-inset-top)+60px)]
  const getBottomPosition = () => {
    if (hasActiveCall) return "bottom-4";
    // Always position chat widget above the dialer
    return !isDialerMinimized ? "bottom-[624px] max-[500px]:bottom-auto max-[500px]:top-[calc(env(safe-area-inset-top)+60px)]" : "bottom-[72px]";
  };

  const getRightPosition = () => {
    if (hasActiveCall) return "left-4";
    return "right-4";
  };

  const positionClass = hasActiveCall ? `${getBottomPosition()} left-4` : `${getBottomPosition()} ${getRightPosition()}`;

  if (isMinimized) {
    // Shell: an iMessage-style glass chip above the dock — blue bubble with
    // the unread dot riding it, the lead's NAME (not a generic "Chat"), and
    // clear of the nav instead of overlapping it.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/m")) {
      return (
        <div
          ref={chipElRef}
          className="fixed z-[60]"
          style={
            chipPos
              ? { left: chipPos.x, top: chipPos.y, touchAction: "none" }
              : { right: 12, bottom: "calc(env(safe-area-inset-bottom, 0px) + 108px)", touchAction: "none" }
          }
          onTouchStart={onChipTouchStart}
        >
          <button
            onClick={() => {
              // a drag that just ended must not count as a tap
              if (chipDraggedRef.current) {
                chipDraggedRef.current = false;
                return;
              }
              onMaximize();
            }}
            className="flex items-center gap-2 rounded-full border border-white/60 bg-white/85 py-1.5 pl-1.5 pr-3.5 shadow-[0_8px_28px_rgba(0,0,0,0.18)] backdrop-blur-xl backdrop-saturate-[180%] transition-transform active:scale-95"
          >
            <span className="relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
              <MessageSquare className="h-4 w-4" />
              {messages.length > 0 && (
                <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-[#34C759] ring-2 ring-white" />
              )}
            </span>
            <span className="max-w-[110px] truncate text-[13px] font-semibold text-black">
              {leadName || "Chat"}
            </span>
          </button>
        </div>
      );
    }
    return (
      <div className={`fixed ${positionClass} z-[60]`}>
        <Button
          onClick={onMaximize}
          className="h-12 px-4 shadow-lg relative bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
        >
          <MessageSquare className="h-5 w-5" />
          <span className="font-medium">Chat</span>
          {messages.length > 0 && (
            <span className="h-2.5 w-2.5 bg-green-400 rounded-full animate-pulse" />
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className={`active-sms-widget fixed ${positionClass} z-[60] w-[400px] max-w-[calc(100vw-1rem)] bg-background border-2 border-blue-500 rounded-lg shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300 shadow-blue-500/50`} style={{ height: '550px', maxHeight: 'calc(100vh - 100px)' }}>
      {/* Header - styled like dialer widget */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50 rounded-t-lg">
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          <MessageSquare className="h-5 w-5 text-blue-600 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-semibold text-sm truncate">{leadName}</h3>
              {/* Tiny phone icon — click to reveal the lead's phone number. Hidden by default
                  so the header is clean; the "From:" indicator below is always visible. */}
              {phoneNumber && (
                <button
                  onClick={() => setShowLeadPhone(!showLeadPhone)}
                  className={`p-0.5 rounded transition-colors ${showLeadPhone ? 'text-blue-600 hover:text-blue-700' : 'text-muted-foreground hover:text-blue-600'}`}
                  title={showLeadPhone ? 'Hide their number' : 'Show their number'}
                >
                  <PhoneIncoming className="h-3 w-3" />
                </button>
              )}
            </div>
            {showLeadPhone && phoneNumber && (
              <p className="text-xs text-muted-foreground truncate">{formatPhoneNumber(phoneNumber)}</p>
            )}
            {/* Always-visible outbound-number indicator. The frontend mirrors the backend's
                priority (A2P-linked number first, then user default, then area-code match) so
                the agent can see exactly which of their numbers the SMS will send from. */}
            {(() => {
              const senderNumber = selectBestOutboundPhone(phoneNumber || '');
              if (!senderNumber) return null;
              return (
                <p className="text-xs text-emerald-600 font-semibold truncate">
                  From: {formatPhoneNumber(senderNumber)}
                </p>
              );
            })()}
          </div>
        </div>
        <div className="flex items-center space-x-1 shrink-0">
          {/* Call Button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
            onClick={handleCall}
            disabled={isInitiatingCall || !phoneNumber}
            title="Call"
          >
            {isInitiatingCall ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Phone className="h-4 w-4" />
            )}
          </Button>
          {leadId && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => {
                // Shell-aware: native lead screen on /m
                if (window.location.pathname.startsWith("/m")) {
                  onMinimize();
                  router.push(`/m/leads/${leadId}`);
                  return;
                }
                router.push(`/leads/${leadId}`);
              }}
              title="View Lead Details"
            >
              <FileText className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={onMinimize}
            title="Minimize"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={onClose}
            title="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages Container - overflow-x-visible to show reaction emojis */}
      <div className="flex-1 overflow-y-auto overflow-x-visible p-4 space-y-4" style={{ minHeight: '300px' }}>
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-8">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages
            // Filter out emoji-only inbound messages that were converted to reactions
            .filter((message) => !processedReactionIds.has(message.id))
            .map((message) => (
            <div
              key={message.id}
              className={`flex ${message.direction === "outbound" ? "justify-end" : "justify-start"} group pb-2`}
            >
              <div className="relative max-w-[75%]">
                <div
                  className={`rounded-lg px-4 py-2 ${
                    message.direction === "outbound"
                      ? "bg-blue-500 text-white"
                      : "bg-white text-gray-900 border border-gray-200"
                  }`}
                >
                  {/* Render MMS media attachments */}
                  {message.mediaUrls && message.mediaUrls.length > 0 && (
                    <div className="mb-2 space-y-2">
                      {message.mediaUrls.map((url, index) => {
                        const proxiedUrl = getProxiedMediaUrl(url);
                        return (
                          <div
                            key={index}
                            className="block cursor-pointer"
                            onClick={() => setLightboxImage(proxiedUrl)}
                          >
                            <img
                              src={proxiedUrl}
                              alt={`MMS attachment ${index + 1}`}
                              className="max-w-full rounded-md max-h-40 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                            <div className={`hidden items-center gap-2 p-2 rounded-md ${
                              message.direction === "outbound" ? "bg-blue-400" : "bg-gray-100"
                            }`}>
                              <ImageIcon className="h-4 w-4" />
                              <span className="text-xs">View Attachment</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Render message text (skip if just placeholder) */}
                  {message.text && message.text !== '[MMS Attachment]' && (
                    <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                  )}
                  <div
                    className={`text-xs mt-1 flex items-center gap-1 ${
                      message.direction === "outbound" ? "text-blue-100" : "text-gray-500"
                    }`}
                  >
                    <span>{formatTime(message.timestamp)}</span>
                    {message.direction === "outbound" && message.status && (
                      <span className="capitalize">• {message.status}</span>
                    )}
                  </div>
                  {message.direction === "outbound" &&
                    (message.status === "failed" || message.status === "undelivered") &&
                    (() => {
                      const reason = getSmsFailureReason(message.errorCode, message.errorMessage);
                      return reason ? (
                        <p className="text-xs text-red-100 mt-0.5" title={reason.hint}>
                          {reason.label}
                        </p>
                      ) : null;
                    })()}
                </div>

                {/* iMessage-style reaction bubbles - shown on top-right of message */}
                {messageReactions[message.id] && messageReactions[message.id].length > 0 && (
                  <div className={`absolute -top-2 ${message.direction === "outbound" ? "-left-2" : "-right-2"} flex items-center z-20`}>
                    <div className="bg-white border border-gray-200 rounded-full shadow-md px-1.5 py-0.5 flex items-center gap-0.5 animate-in zoom-in-50 duration-200">
                      {messageReactions[message.id].map((emoji, idx) => (
                        <span key={idx} className="text-sm">{emoji}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reaction picker - appears on hover for inbound messages */}
                {message.direction === "inbound" && (
                  <div className="absolute -bottom-1 left-0 translate-y-full opacity-0 group-hover:opacity-100 transition-all duration-200 z-10">
                    <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-full shadow-lg p-1 flex gap-0.5">
                      {REACTION_EMOJIS.map((emoji) => (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(emoji, message.id)}
                          className={`hover:bg-gray-100 rounded-full w-7 h-7 flex items-center justify-center text-base transition-all hover:scale-110 active:scale-95 ${
                            messageReactions[message.id]?.includes(emoji) ? 'bg-blue-100 ring-2 ring-blue-300' : ''
                          }`}
                          title={messageReactions[message.id]?.includes(emoji) ? `Remove ${emoji}` : `React with ${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Replies Panel - positioned above input, scrollable */}
      {showQuickReplies && (
        <div className="px-4 py-2 border-t border-gray-200 flex-shrink-0 max-h-28 overflow-y-auto bg-muted/30">
          <div className="flex flex-wrap gap-2">
            {QUICK_REPLIES.map((reply) => (
              <button
                key={reply}
                onClick={() => handleQuickReply(reply)}
                className="px-3 py-1.5 text-xs bg-background border border-gray-200 rounded-full hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-colors"
              >
                {reply}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="p-3 border-t bg-muted/50 rounded-b-lg flex-shrink-0 space-y-2">
        {/* A2P Verification Warning */}
        {!a2pStatus.isLoading && !a2pStatus.canSend ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">{A2P_STAGE_TITLE[((a2pStatus as any).stage || 'required') as keyof typeof A2P_STAGE_TITLE]}</p>
                <p className="text-xs text-amber-700 mt-1">
                  {a2pStatus.reason || "Complete your A2P registration to send SMS messages to clients."}
                </p>
              </div>
            </div>
            <Link
              href="/settings?tab=a2p-verification"
              className="block w-full"
            >
              <Button
                variant="outline"
                className="w-full bg-amber-100 hover:bg-amber-200 border-amber-300 text-amber-800 h-9"
              >
                {(a2pStatus as any).stage === 'pending' ? 'View A2P Status' : 'Complete A2P Verification'}
              </Button>
            </Link>
          </div>
        ) : (
          <>
        {/* Templates and Quick Replies Row */}
        <div className="flex gap-2 items-center">
          <SelectTemplateButton
            onTemplateSelect={handleTemplateSelect}
            selectedTemplateId={selectedTemplateId}
            className="flex-1 text-xs h-8"
          />
          <Button
            variant="outline"
            size="sm"
            className={`h-8 text-xs ${showQuickReplies ? 'bg-blue-50 border-blue-300 text-blue-600' : ''}`}
            onClick={() => setShowQuickReplies(!showQuickReplies)}
          >
            Quick Replies
          </Button>
        </div>

        {/* Attachment Preview */}
        {attachedFile && (
          <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
            {attachedPreview ? (
              <img src={attachedPreview} alt="Preview" className="h-12 w-12 object-cover rounded" />
            ) : (
              <div className="h-12 w-12 bg-blue-100 rounded flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{attachedFile.name}</p>
              <p className="text-xs text-gray-500">{(attachedFile.size / 1024).toFixed(1)} KB</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemoveAttachment}
              className="h-8 w-8 p-0 text-gray-500 hover:text-red-500"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* TCPA calling-hours lock — texting disabled outside 8am–9pm the lead's
            local time (Sold/UWR/appt exempt). */}
        {textingBlocked && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
            <span>🕒</span>
            <span>
              Can&rsquo;t text right now — it&rsquo;s {hoursStatus?.localTime} for this lead
              {hoursStatus?.timezone ? ` (${hoursStatus.timezone})` : ""}. Texting is allowed
              8am–9pm in the lead&rsquo;s local time.
            </span>
          </div>
        )}

        {/* Message Input Row */}
        <div className="flex gap-2 items-stretch">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/mp4,video/3gpp,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
          {/* Attachment button */}
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            // `textingBlocked` added 2026-07-29. The send button was already
            // gated on it, but attaching was NOT — so outside the lead's
            // 8am–9pm window an agent could pick a file, watch it upload, and
            // then find Send dead with no explanation tied to the attachment.
            // Reported as "the MMS uploaded but I couldn't hit send". Blocking
            // the attach up front means nobody does work they can't complete.
            disabled={sending || !!attachedFile || textingBlocked}
            className="h-[52px] w-[52px] p-0 flex items-center justify-center flex-shrink-0 [&_svg]:!size-6"
            title={
              textingBlocked
                ? "Can't attach right now — outside this lead's texting hours (8am–9pm their time)"
                : attachedFile
                  ? "Remove the current attachment first"
                  : "Attach image or file"
            }
          >
            <Paperclip className="!h-6 !w-6" />
          </Button>
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={textingBlocked ? "Outside calling hours (8am–9pm their time)" : attachedFile ? "Add a caption... (optional)" : "Type message... (Enter to send)"}
            rows={2}
            className="flex-1 resize-none text-sm bg-background min-h-[52px]"
            disabled={sending || textingBlocked}
          />
        </div>

        {/* Send Button + Contact Card */}
        <div className="flex gap-2">
        <Button
          onClick={handleSendMessage}
          disabled={sending || textingBlocked || (!newMessage.trim() && !attachedFile)}
          className="flex-1 bg-blue-600 hover:bg-blue-700 h-10 flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Uploading...</span>
            </>
          ) : sending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Sending...</span>
            </>
          ) : textingBlocked ? (
            <span>Outside calling hours</span>
          ) : (
            <>
              <Send className="h-4 w-4" />
              <span>Send {attachedFile ? 'MMS' : 'Message'}</span>
            </>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={handleSendContactCard}
          disabled={sending}
          className="h-10 w-10 p-0 flex items-center justify-center flex-shrink-0"
          title="Send Contact Card"
        >
          <ContactRound className="h-4 w-4" />
        </Button>
        </div>
          </>
        )}
      </div>

      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={lightboxImage}
            alt="Full size attachment"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
